import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import QuizNodeActivityEditor from '../../NodeActivityEditor/nodes/QuizNodeActivityEditor';
import RemoveIcon from '../../../../public/assets/images/editor/delete-icon-black.svg';
import MoveUpIcon from '../../../icons/move-up-black.svg';
import UploadImageIcon from '../../../icons/upload-image.svg';
import {
  ContentSection,
  Questions,
  QuestionWrapper,
  Question,
  QuestionTools,
  DeleteQuestion,
  MoveUpQuestion,
  MoveDownQuestion,
  InsertImage,
} from '../styles';

const QuizContentEditor = ({
  children,
  data,
  payload,
  setPayload,
  getUpdates,
  type,
  fileService,
  loading = false,
  updating = false,
}) => {
  const isUpdating = loading || updating;
  const useAutoSave = window.editor.useAutoSave;

  const mapQuestions = (questions) => {
    return (questions || []).map((question) => ({
      ...question,
      alternatives: [...question.QuizAlternatives],
      QuizAlternatives: [],
    }));
  };

  const mapAlternatives = (questions) => {
    return questions.reduce((total, question) => total.concat(question.alternatives), []);
  };

  const mapAlternativeIds = (questions) => mapAlternatives(questions).map((alternative) => alternative.id);

  const [questions, setQuestions] = useState(mapQuestions(data?.QuizQuestions || []));

  const addQuestion = (event) => {
    const newQuestions = [...(event?.detail?.questions || [])];
    newQuestions.push({ body: '', order: newQuestions.length, alternatives: [] });
    setQuestions(newQuestions);
  };

  const addAlternative = (parentIndex) => {
    if (isUpdating) {
      return;
    }
    const newAlternatives = [...(questions[parentIndex]?.alternatives || [])];
    newAlternatives.push({ body: '', order: newAlternatives.length });
    const newQuestions = [...questions];
    newQuestions[parentIndex].alternatives = newAlternatives;
    setQuestions(newQuestions);
  };

  const removeAlternative = (parentIndex, index) => {
    if (isUpdating) {
      return;
    }
    const newAlternatives = (questions[parentIndex]?.alternatives || [])
      .filter((alternative) => alternative.order !== index)
      .map((alternative, index) => ({ ...alternative, order: index }));
    const newQuestions = [...questions];
    newQuestions[parentIndex].alternatives = newAlternatives;
    setQuestions(newQuestions);
  };

  const markAlternative = (parentIndex, markedIndex) => {
    if (isUpdating) {
      return;
    }
    const newQuestions = [...questions];
    newQuestions[parentIndex].alternatives = (newQuestions[parentIndex]?.alternatives || []).map(
      (alternative, index) => ({
        ...alternative,
        correct: markedIndex === index,
      }),
    );
    setQuestions(newQuestions);
  };

  const getMissingId = (questions) => {
    const result = { questionIndex: -1, alternativeIndex: -1 };
    questions.forEach((question, questionIndex) => {
      if (!question.id) {
        result.questionIndex = questionIndex;
      }
      (question?.alternatives || []).forEach((alternative, alternativeIndex) => {
        if (!alternative.id) {
          result.questionIndex = questionIndex;
          result.alternativeIndex = alternativeIndex;
        }
      });
    });
    return result;
  };

  useEffect(() => {
    const { questionIndex, alternativeIndex } = getMissingId(questions);
    const newQuestions = [...questions];
    if (questionIndex !== -1 && alternativeIndex === -1 && newQuestions[questionIndex]) {
      newQuestions[questionIndex] = { ...data.QuizQuestions[questionIndex], order: questionIndex, alternatives: [] };
      setQuestions(newQuestions);
    } else if (
      questionIndex !== -1 &&
      alternativeIndex !== -1 &&
      newQuestions[questionIndex].alternatives[alternativeIndex]
    ) {
      newQuestions[questionIndex].alternatives[alternativeIndex] = {
        ...data.QuizQuestions[questionIndex]?.QuizAlternatives[alternativeIndex],
        order: alternativeIndex,
      };
      setQuestions(newQuestions);
    }
  }, [JSON.stringify(data.QuizQuestions)]);

  useEffect(() => {
    document.addEventListener('question.added', addQuestion);
    return () => {
      document.removeEventListener('question.added', addQuestion);
    };
  }, []);

  useEffect(() => {
    setPayload({ ...payload, questions });
  }, []);

  useEffect(() => {
    if (
      questions?.length > data?.QuizQuestions?.length ||
      questions?.findLast((x) => x)?.alternatives?.length >
        data?.QuizQuestions?.findLast((x) => x)?.QuizAlternatives?.length
    ) {
      document.querySelector('.content-panel')?.scrollTo(0, 10000000);
    }
  }, [questions]);

  const getDeletedAlternatives = () => {
    const diff = (a, b) => a.filter((i) => b.indexOf(i) === -1);
    const oldIds = mapAlternativeIds(mapQuestions(data?.QuizQuestions));
    const newIds = mapAlternativeIds(questions);
    return diff(oldIds, newIds);
  };

  useEffect(() => {
    const {
      itemsAddedOrRemoved,
      itemsUpdated,
      deletedIds: deletedQuestionIds,
    } = getUpdates(mapQuestions(data?.QuizQuestions), questions);
    if (!isUpdating && (itemsAddedOrRemoved || itemsUpdated || payload?.updated)) {
      const deletedAlternativeIds = getDeletedAlternatives();
      setPayload({
        ...payload,
        questions,
        deletions: { questions: deletedQuestionIds, alternatives: deletedAlternativeIds },
        updated: false,
        immediateSave: !payload?.updated,
      });
    }
  }, [JSON.stringify(questions)]);

  const isOrderDisabled = (index, delta) =>
    loading || (index === 0 && delta === -1) || (index === questions.length - 1 && delta === 1);

  const removeQuestion = (index) => {
    if (isUpdating) {
      return;
    }
    const newQuestions = questions
      .filter((question) => question.order !== index)
      .map((question, index) => ({ ...question, order: index }));
    setQuestions(newQuestions);
  };

  const orderQuestion = (index, delta) => {
    if (isUpdating || isOrderDisabled(index, delta)) {
      return;
    }
    const newQuestions = [...questions];
    newQuestions[index].order = index + delta;
    newQuestions[index + delta].order = index;
    setQuestions(newQuestions);
  };

  const uploadImage = (index) => {
    if (isUpdating) {
      return;
    }
    const fileUpload = document.querySelector(`#file_question_editor_${index}`);
    fileUpload.click();
  };

  const sortByOrder = (a, b) => a.order - b.order;

  const onChange = (value, index, parentIndex) => {
    const newQuestions = [...questions];
    if (parentIndex !== undefined && parentIndex !== null) {
      newQuestions[parentIndex].alternatives[index].body = value;
    } else {
      newQuestions[index].body = value;
    }
    setPayload({ ...payload, updated: true });
    setQuestions(newQuestions);
  };

  const isDisabled = (id) => useAutoSave && !id;

  const removeLegacyMedia = (index, parentIndex) => {
    const newQuestions = [...questions];
    if (parentIndex !== undefined && parentIndex !== null) {
      newQuestions[parentIndex].alternatives[index].media_preview = null;
      newQuestions[parentIndex].alternatives[index].media_mime = null;
      newQuestions[parentIndex].alternatives[index].media_reference = null;
    } else {
      newQuestions[index].media_preview = null;
      newQuestions[index].media_mime = null;
      newQuestions[index].media_reference = null;
    }
    setPayload({ ...payload, updated: true });
    setQuestions(newQuestions);
  };

  const mappedQuestions = questions.sort(sortByOrder).map((question, index) => (
    <QuestionWrapper key={`question_${index}`}>
      <Question>
        <QuizNodeActivityEditor
          question={question}
          index={index}
          onChange={onChange}
          type={type}
          fileService={fileService}
          loading={!!loading}
          disabled={loading || isDisabled(question.id)}
          addAlternative={addAlternative}
          removeAlternative={removeAlternative}
          markAlternative={markAlternative}
          removeLegacyMedia={removeLegacyMedia}
        />
      </Question>
      <QuestionTools>
        <InsertImage
          src={UploadImageIcon}
          title={i18n.__('toolbar_button_tooltip_image')}
          onClick={() => uploadImage(index)}
          disabled={loading}
        />
        <DeleteQuestion
          src={RemoveIcon}
          title={i18n.__('app_delete')}
          onClick={() => removeQuestion(index)}
          disabled={loading}
        />
        <MoveUpQuestion
          src={MoveUpIcon}
          title={i18n.__('app_move_up')}
          onClick={() => orderQuestion(index, -1)}
          disabled={isOrderDisabled(index, -1)}
        />
        <MoveDownQuestion
          src={MoveUpIcon}
          title={i18n.__('app_move_down')}
          onClick={() => orderQuestion(index, 1)}
          disabled={isOrderDisabled(index, +1)}
        />
      </QuestionTools>
    </QuestionWrapper>
  ));

  return (
    <ContentSection>
      {children}
      <Questions>{mappedQuestions}</Questions>
    </ContentSection>
  );
};

export default QuizContentEditor;

QuizContentEditor.propTypes = {
  children: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  payload: PropTypes.object.isRequired,
  setPayload: PropTypes.func.isRequired,
  getUpdates: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  fileService: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  updating: PropTypes.bool,
};
